import { useState, type FormEvent } from "react";
import { api } from "../api/client";
import type { Cadence, RecurringAllowance } from "../api/types";
import { useErrorHandler } from "../auth/AuthContext";
import {
  formatCents,
  isoFromLocalParts,
  localPartsFromIso,
  parseDollarsToCents,
  tzLabel,
} from "../lib/money";
import { ErrorMessage, Field, Modal, Segmented } from "./common";

type Start = "now" | "scheduled";

const CADENCES: { value: Cadence; label: string }[] = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
];

/**
 * Create or edit a recurring allowance.
 *
 * The parent sets how much and how often — and nothing else. Where the money
 * lands is the child's decision, made with the Spend/Save slider on their own
 * dashboard, so there is deliberately no bucket picker here.
 */
export function AllowanceModal({
  childId,
  childName,
  existing,
  onClose,
  onSaved,
}: {
  childId: string;
  childName: string;
  existing: RecurringAllowance | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toMessage = useErrorHandler();

  const [amount, setAmount] = useState(
    existing ? formatCents(existing.amountCents).replace("$", "") : "",
  );
  const [cadence, setCadence] = useState<Cadence>(existing?.cadence ?? "weekly");
  const [note, setNote] = useState(existing?.note ?? "");
  // A new allowance can start now — paying immediately — or on a chosen date.
  // Editing does not offer this: moving an existing schedule is what Pause and
  // the next-run date are for.
  const [start, setStart] = useState<Start>("now");
  const [startDate, setStartDate] = useState(
    () => localPartsFromIso(new Date().toISOString()).date,
  );
  const [startTime, setStartTime] = useState("08:00");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");

    const cents = parseDollarsToCents(amount);
    if (cents === null || cents <= 0) {
      setError("Enter an amount, like 5.00");
      return;
    }

    setSaving(true);
    try {
      if (existing) {
        await api.updateAllowance(existing.id, {
          amountCents: cents,
          cadence,
          note,
        });
      } else {
        const startAt =
          start === "now"
            ? undefined
            : isoFromLocalParts(startDate, startTime);
        if (start === "scheduled" && !startAt) {
          setError("Pick a start date.");
          setSaving(false);
          return;
        }
        await api.createAllowance(childId, {
          amountCents: cents,
          cadence,
          note,
          ...(startAt ? { startAt } : {}),
        });
      }
      onSaved();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={
        existing ? "Edit allowance" : `Set up an allowance for ${childName}`
      }
      onClose={onClose}
    >
      <form className="stack" onSubmit={submit}>
        <ErrorMessage>{error}</ErrorMessage>

        <Field label="Amount" hint="For example 5 or 12.50">
          <input
            className="input"
            inputMode="decimal"
            autoFocus
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>

        <Field label="How often">
          <select
            className="select"
            value={cadence}
            onChange={(e) => setCadence(e.target.value as Cadence)}
          >
            {CADENCES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>

        {existing ? null : (
          <>
            <Field label="Starting">
              <Segmented<Start>
                value={start}
                onChange={setStart}
                options={[
                  { value: "now", label: "Now" },
                  { value: "scheduled", label: "Pick a date" },
                ]}
              />
            </Field>

            {start === "scheduled" ? (
              <Field
                label="First payment"
                hint={`The schedule runs from this date and time (${tzLabel(
                  isoFromLocalParts(startDate, startTime),
                )}). Later payments keep the same time of day.`}
              >
                <div className="row row--fields">
                  <input
                    className="input"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <input
                    className="input"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
              </Field>
            ) : null}
          </>
        )}

        <Field label="Note">
          <input
            className="input"
            placeholder="Weekly allowance"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <p className="muted">
          {childName} chooses how much of this goes to Spend and how much to
          Save.
        </p>

        <div className="row row--between">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : existing ? "Save changes" : "Start it"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
